import { useIntl } from "react-intl";
import { SimpleEntityGridColumn } from "src/app/components/grid/SimpleEntityGrid";
import { CellLinks, CellTags, UserCell } from "src/app/components";
import {
    infinityDateColDef,
    infinityNumberColDef,
    infinitySelectColDef,
    infinityTextColDef,
} from "src/app/utils/aggrid/infinityLoadHelper";
import { SetFilterValuesFuncParams } from "ag-grid-enterprise";

export interface IValuesAsync {
    getManager: (params: SetFilterValuesFuncParams) => void;
    getMoneySite: (params: SetFilterValuesFuncParams) => void;
    getRd: (params: SetFilterValuesFuncParams) => void;
    getWritingType: (params: SetFilterValuesFuncParams) => void;
}

export default function useReviewedGridColumns(valuesAsync: IValuesAsync) {
    const { formatMessage } = useIntl();

    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "id",
            ...infinityNumberColDef("id"),
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "publishedAt",
            ...infinityDateColDef(
                formatMessage({ id: "content.grid.col.publishedAt" }),
                "publishedAt"
            ),
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "manager.firstName*manager.lastName",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.manager" }),
                valuesAsync.getManager
            ),
            valueGetter: ({ data }: any) =>
                data?.manager ? `${data.manager.firstName} ${data.manager.lastName}` : "",
            cellRenderer: ({ data }: any) => <UserCell user={data.manager} />,
            autoHeight: true,
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "moneySite.domain#platform.name",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.moneySite" }),
                valuesAsync.getMoneySite
            ),
            valueGetter: ({ data }) =>
                data?.platform
                    ? data?.platform?.name
                    : data?.moneySite
                    ? `${data?.moneySite.domain} (${data?.moneySite.pbn.name})`
                    : "N/A",
            minWidth: 200,
            maxWidth: 200,
        },
        {
            field: "rd.domain",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.publishedOn" }),
                valuesAsync.getRd
            ),
            minWidth: 200,
            maxWidth: 200,
        },
        {
            field: "reviewedAt",
            ...infinityDateColDef(
                formatMessage({ id: "content.grid.col.reviewedAt" }),
                "reviewedAt"
            ),
            minWidth: 100,
            maxWidth: 100,
            sort: "desc",
        },
        {
            field: "proofreader.firstName#proofreader.lastName",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.proofreader" })),
            headerName: formatMessage({ id: "content.grid.col.proofreader" }),
            valueGetter: ({ data }) =>
                data?.proofreader
                    ? `${data.proofreader.firstName} ${data.proofreader.lastName}`
                    : "",
            cellRenderer: ({ data }: any) => <UserCell user={data.proofreader} />,
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "rate",
            ...infinityNumberColDef(formatMessage({ id: "content.grid.col.rate" })),
            minWidth: 80,
            maxWidth: 80,
        },
        {
            field: "commentFromProofreader",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.commentFromProofreader" })),
            width: 120,
        },
        {
            field: "writerPrice",
            ...infinityNumberColDef(formatMessage({ id: "delivered.grid.col.writerPrice" })),
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "thematic.name",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.thematic" })),
            minWidth: 107,
            maxWidth: 107,
        },
        {
            field: "contentType*content.writingType",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.contentType" }),
                valuesAsync.getWritingType
            ),
            valueFormatter: ({ data }: any) =>
                `${formatMessage({
                    id: `content.grid.col.contentType.${data.contentType}`,
                })} ${formatMessage({
                    id: `content.grid.col.writingType.${data.content.writingType}`,
                })}`,
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "wordCount",
            ...infinityNumberColDef(formatMessage({ id: "delivered.grid.col.wordCount" })),
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "keyword",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.contentType" })),
            autoHeight: true,
            valueGetter: ({ data }) => data?.keywords,
            cellRenderer: ({ data }: any) => <CellTags tags={data?.keywords} />,
            width: 200,
            hide: true,
        },
        {
            field: "links.url#links.anchor",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.links" })),
            valueGetter: ({ data }: any) =>
                data?.links
                    ? data.links[1]
                        ? `${data.links[0].url} (${data.links[0].anchor})\n ${data.links[1].url} (${data.links[1].anchor})`
                        : `${data.links[0].url} (${data.links[0].anchor})`
                    : "",
            cellRenderer: ({ data }: any) => <CellLinks links={data?.links} />,
            autoHeight: true,
            width: 360,
        },
        {
            field: "content.title",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.title" })),
            hide: true,
        },
        {
            field: "commentForWriter",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.commentForWriter" })),
            hide: true,
        },
        {
            field: "createdAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.createdAt" }), "createdAt"),
            minWidth: 130,
            maxWidth: 130,
            hide: true,
        },
        {
            field: "updatedAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.updatedAt" }), "updatedAt"),
            minWidth: 130,
            maxWidth: 130,
            hide: true,
        },
    ];
    return gridColumns;
}
